import { useEffect, useRef, useCallback } from 'react';
import hybridAuthService from '../services/hybridAuthService';

const useHybridSession = (onLogout) => {
  const activityTimeoutRef = useRef(null);
  
  // For testing: 30 seconds of inactivity (30 seconds)
  // For production: 60 minutes (3600000 milliseconds)
             const INACTIVITY_TIMEOUT = 3600000; // 60 minutes (1 hour)
  
  // Check if session has expired on mount
  const checkSessionExpiry = useCallback(async () => {
    if (hybridAuthService.isSessionExpired()) {
      await hybridAuthService.logout();
      onLogout();
      return false;
    }
    return true;
  }, [onLogout]);

  // Reset activity timer
  const resetActivityTimer = useCallback(() => {
    // Update last activity time
    hybridAuthService.updateLastActivityTime();
    
    // Clear existing timeout
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    // Set final timeout (30 seconds from now)
    activityTimeoutRef.current = setTimeout(async () => {
      await hybridAuthService.logout();
      onLogout();
    }, INACTIVITY_TIMEOUT);
  }, [onLogout]);

  // Handle user activity
  const handleUserActivity = useCallback(() => {
    resetActivityTimer();
  }, [resetActivityTimer]);

  // Initialize session management
  useEffect(() => {
    const initializeSession = async () => {
      const hasLocalSession = hybridAuthService.isAuthenticated();
      const hasSupabaseSession = await hybridAuthService.hasActiveSupabaseSession();

      if (!hasLocalSession && !hasSupabaseSession) {
        return;
      }

      const isValid = await checkSessionExpiry();
      if (!isValid) {
        return;
      }

      // Set up activity listeners
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      events.forEach(event => {
        document.addEventListener(event, handleUserActivity, true);
      });

      // Start activity timer
      resetActivityTimer();
    };

    initializeSession();

    // Cleanup function
    return () => {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [handleUserActivity, resetActivityTimer, checkSessionExpiry]);

  return {
    resetActivityTimer,
    handleUserActivity
  };
};

export default useHybridSession; 